import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  onSnapshot,
  Unsubscribe,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type {
  Proposal,
  DashboardFilters,
  ResourceType,
  Technology,
} from "@/features/Dashboard";

class DashboardService {
  private readonly proposalsCollection = collection(db, "proposals");
  private readonly resourceTypesCollection = collection(db, "resourceTypes");
  private readonly technologiesCollection = collection(db, "technologies");

  // ✅ Buscar propostas com filtros (sem índices compostos)
  async getProposals(filters?: DashboardFilters): Promise<Proposal[]> {
    try {
      // Se não há filtros, buscar todas com ordenação simples
      if (!filters || (!filters.heatLevel && !filters.status)) {
        const q = query(this.proposalsCollection, orderBy("createdAt", "desc"));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            } as Proposal)
        );
      }

      // Se há filtros, buscar sem ordenação e ordenar no cliente
      let q = query(this.proposalsCollection);

      if (filters.heatLevel) {
        q = query(q, where("heatLevel", "==", filters.heatLevel));
      }

      if (filters.status) {
        q = query(q, where("status", "==", filters.status));
      }

      const snapshot = await getDocs(q);
      const proposals = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Proposal)
      );

      // Ordenar no cliente (JavaScript)
      return proposals.sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // Ordem decrescente
      });
    } catch (error) {
      console.error("Erro ao buscar propostas:", error);
      throw new Error("Erro ao carregar propostas");
    }
  }

  // ✅ Escutar mudanças (sem índices compostos)
  subscribeToProposals(
    callback: (proposals: Proposal[]) => void,
    onError: (error: string) => void,
    filters?: DashboardFilters
  ): Unsubscribe {
    try {
      // Query simples sem ordenação para evitar índice composto
      let q = query(this.proposalsCollection);

      if (filters?.heatLevel) {
        q = query(q, where("heatLevel", "==", filters.heatLevel));
      }

      if (filters?.status) {
        q = query(q, where("status", "==", filters.status));
      }

      return onSnapshot(
        q,
        (querySnapshot) => {
          const proposals = querySnapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as Proposal)
          );

          // Ordenar no cliente
          const sortedProposals = proposals.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();
            return dateB - dateA;
          });

          callback(sortedProposals);
        },
        (error) => {
          console.error("Erro ao escutar propostas:", error);
          onError("Erro ao carregar propostas em tempo real.");
        }
      );
    } catch (error) {
      console.error("Erro na subscrição:", error);
      onError("Erro ao configurar subscrição de propostas.");
      return () => {}; // Retorna função vazia se houver erro
    }
  }

  // ✅ Buscar propostas relevantes (sem índice composto)
  async getRelevantProposals(): Promise<Proposal[]> {
    try {
      // Buscar todas as propostas sem filtro para evitar índice composto
      const q = query(this.proposalsCollection);
      const snapshot = await getDocs(q);

      const allProposals = snapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          } as Proposal)
      );

      // Filtrar e ordenar no cliente
      const heatLevels = ["warm", "hot", "burning"];
      const relevantProposals = allProposals
        .filter((proposal) => heatLevels.includes(proposal.heatLevel))
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || 0).getTime();
          const dateB = new Date(b.createdAt || 0).getTime();
          return dateB - dateA;
        });

      return relevantProposals;
    } catch (error) {
      console.error("Erro ao buscar propostas relevantes:", error);
      throw new Error("Erro ao carregar propostas relevantes");
    }
  }

  // ✅ Buscar tipos de recursos
  async getResourceTypes(): Promise<Record<string, ResourceType>> {
    try {
      const snapshot = await getDocs(this.resourceTypesCollection);
      const types: Record<string, ResourceType> = {};

      snapshot.docs.forEach((doc) => {
        types[doc.id] = {
          id: doc.id,
          name: doc.data().name || doc.id,
        };
      });

      return types;
    } catch (error) {
      console.error("Erro ao buscar tipos de recursos:", error);
      throw new Error("Erro ao carregar tipos de recursos");
    }
  }

  // ✅ Buscar tecnologias
  async getTechnologies(): Promise<Record<string, Technology>> {
    try {
      const snapshot = await getDocs(this.technologiesCollection);
      const technologies: Record<string, Technology> = {};

      snapshot.docs.forEach((doc) => {
        technologies[doc.id] = {
          id: doc.id,
          name: doc.data().name || doc.id,
          typeId: doc.data().typeId || "",
        };
      });

      return technologies;
    } catch (error) {
      console.error("Erro ao buscar tecnologias:", error);
      throw new Error("Erro ao carregar tecnologias");
    }
  }

  // Métodos auxiliares mantidos
  async getResourceTypeNames(): Promise<Record<string, string>> {
    const types = await this.getResourceTypes();
    const typeNames: Record<string, string> = {};

    Object.entries(types).forEach(([id, type]) => {
      typeNames[id] = type.name;
    });

    return typeNames;
  }

  async getTechnologyNames(): Promise<Record<string, string>> {
    const technologies = await this.getTechnologies();
    const techNames: Record<string, string> = {};

    Object.entries(technologies).forEach(([id, tech]) => {
      techNames[id] = tech.name;
    });

    return techNames;
  }

  // Calcular estatísticas do dashboard
  calculateDashboardStats(proposals: Proposal[]) {
    const relevantProposals = proposals.filter((proposal) =>
      ["warm", "hot", "burning"].includes(proposal.heatLevel)
    );

    const totalResources = relevantProposals.reduce(
      (total, proposal) =>
        total +
        proposal.resources.reduce(
          (sum, resource) => sum + resource.quantity,
          0
        ),
      0
    );

    const proposalsByHeatLevel = relevantProposals.reduce((acc, proposal) => {
      acc[proposal.heatLevel] = (acc[proposal.heatLevel] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return {
      totalProposals: relevantProposals.length,
      totalResources,
      proposalsByHeatLevel,
      averageResourcesPerProposal:
        relevantProposals.length > 0
          ? Math.round(totalResources / relevantProposals.length)
          : 0,
    };
  }
}

export const dashboardService = new DashboardService();
